import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { VMC } from "@/components/site/DecorativeBG";

const INDIA = { lat: 19.076, lng: 72.8777, name: "Mumbai · India" };

const DESTINATIONS = [
  { lat: 51.1657, lng: 10.4515, name: "Europe", color: "#1E88E5" },
  { lat: 54.5973, lng: -2.4376, name: "United Kingdom", color: "#52B54B" },
  { lat: 39.8283, lng: -98.5795, name: "United States", color: "#F5A623" },
  { lat: 23.4241, lng: 53.8478, name: "Middle East", color: "#52B54B" },
];

export default function GlobalReach() {
  const containerRef = useRef(null);
  const globeRef = useRef(null);
  const [dim, setDim] = useState({ w: 600, h: 600 });
  const [GlobeComp, setGlobeComp] = useState(null);

  useEffect(() => {
    let mounted = true;
    import("react-globe.gl").then((mod) => {
      if (mounted) setGlobeComp(() => mod.default);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const w = Math.min(rect.width, 340);
      const h = Math.min(w, 320);
      setDim({ w, h });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [GlobeComp]);

  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;
    try {
      g.controls().autoRotate = true;
      g.controls().autoRotateSpeed = 0.6;
      g.controls().enableZoom = false;
      g.pointOfView({ lat: 22, lng: 50, altitude: 2.2 }, 0);
    } catch (e) {
      // ignore
    }
  }, [GlobeComp]);

  const arcs = DESTINATIONS.map((d) => ({
    startLat: INDIA.lat,
    startLng: INDIA.lng,
    endLat: d.lat,
    endLng: d.lng,
    color: [d.color, "#F5A623"],
  }));

  const points = [
    { ...INDIA, size: 0.9, color: "#F5A623" },
    ...DESTINATIONS.map((d) => ({ ...d, size: 0.7 })),
  ];

  return (
    <section
      id="global-reach"
      data-testid="global-reach-section"
      className="relative py-24 lg:py-32 bg-white overflow-hidden"
    >
      <div className="absolute inset-0 bp-grid-dark opacity-30 pointer-events-none" />
      {/* Vertical decorative graphic — far right, sits beside the globe */}
      <VMC
        className="top-1/2 -translate-y-1/2 right-2 lg:right-6 xl:right-10 hidden lg:block [&_svg]:!w-[14rem]"
        opacity={0.22}
        tone="maroon"
        bounce
      />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* LEFT: Heading + content + destinations + callout */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="divider-rule" />
              <span className="eyebrow">Global Reach</span>
            </div>
            <h2 className="h-section">
              Defining the bridge between
              <br />
              <span className="text-brand-blue">
                global markets and Indian manufacturing.
              </span>
            </h2>

            <p className="mt-6 text-brand-text/75 text-base lg:text-lg max-w-xl leading-relaxed">
              From a single engineering hub in India, we manufacture and
              deliver precision components to OEMs across Europe, United
              Kingdom, United States and Middle East — with consolidated
              logistics, compliant documentation and predictable lead times.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-4">
              {DESTINATIONS.map((d) => (
                <div
                  key={d.name}
                  data-testid={`route-${d.name.split(" ")[0].toLowerCase()}`}
                  className="flex items-center gap-3 border border-gray-200 bg-white px-4 py-3 lift-on-hover hover:shadow-lg"
                >
                  <span
                    className="h-2.5 w-2.5"
                    style={{ backgroundColor: d.color }}
                  />
                  <span className="text-sm font-semibold text-brand-navy">
                    {d.name}
                  </span>
                </div>
              ))}
            </div>

            {/* Bridge callout */}
            <div className="mt-10 bg-brand-soft border-l-4 border-brand-orange p-5 lg:p-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-orange font-semibold mb-2">
                The Tirupati Bridge
              </div>
              <p className="text-brand-navy font-medium leading-relaxed">
                We act as a reliable bridge between global markets and Indian
                manufacturing excellence — delivering quality, value and trust
                across borders.
              </p>
            </div>
          </motion.div>

          {/* RIGHT: Bridge banner image + Globe stacked directly below it */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5 flex flex-col items-center lg:pt-12"
          >
            <picture>
              <source srcSet="/global-bridge-bg.webp" type="image/webp" />
              <img
                src="/global-bridge-bg.jpg"
                alt="Bridge between global markets and Indian manufacturing"
                data-testid="global-bridge-image"
                className="w-full max-w-md lg:max-w-full h-auto object-contain rounded-sm shadow-[0_20px_55px_rgba(11,46,89,0.18)]"
              />
            </picture>

            {/* Globe — sized smaller, sits just below the bridge image */}
            <div
              ref={containerRef}
              className="relative w-full mt-4 lg:mt-6 flex items-center justify-center"
              style={{ minHeight: "320px" }}
            >
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-brand-earth/5 via-transparent to-brand-orange/5 blur-3xl" />

              {GlobeComp ? (
                <div className="relative" style={{ zIndex: 2 }}>
                  <GlobeComp
                    ref={globeRef}
                    width={dim.w}
                    height={dim.h}
                    backgroundColor="rgba(0,0,0,0)"
                    globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
                    atmosphereColor="#1E88E5"
                    atmosphereAltitude={0.18}
                    arcsData={arcs}
                    arcColor={"color"}
                    arcDashLength={0.45}
                    arcDashGap={1.2}
                    arcDashAnimateTime={2200}
                    arcStroke={0.4}
                    arcAltitudeAutoScale={0.4}
                    pointsData={points}
                    pointColor={"color"}
                    pointAltitude={0.02}
                    pointRadius={"size"}
                    pointsMerge={false}
                  />
                </div>
              ) : (
                <div className="text-brand-text/50 text-sm">Loading globe…</div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
